import { useEffect, useState, useCallback } from 'react';
import { getDeviceRepository } from '../container';
import type { MediaDeviceDescriptor } from '@shared/types';

interface UseDeviceListResult {
  videoInputs: MediaDeviceDescriptor[];
  audioInputs: MediaDeviceDescriptor[];
  refresh: () => Promise<void>;
  isLoading: boolean;
}

/**
 * Enumerates available camera/microphone devices and keeps the list fresh
 * when devices are plugged/unplugged. Pure presentation hook — all actual
 * enumeration logic lives behind DeviceRepository.
 */
export function useDeviceList(): UseDeviceListResult {
  const repository = getDeviceRepository();
  const [videoInputs, setVideoInputs] = useState<MediaDeviceDescriptor[]>([]);
  const [audioInputs, setAudioInputs] = useState<MediaDeviceDescriptor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    const [video, audio] = await Promise.all([
      repository.listVideoInputs(),
      repository.listAudioInputs(),
    ]);
    setVideoInputs(video);
    setAudioInputs(audio);
    setIsLoading(false);
  }, [repository]);

  useEffect(() => {
    void refresh();
    return repository.onDeviceChange(() => void refresh());
  }, [repository, refresh]);

  return { videoInputs, audioInputs, refresh, isLoading };
}
